import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';

import { ChatType } from '@anyx/common-graphql';


import { GET_STORE_AI_AUTO_REPLY_SETTING } from '../graphql';

import { usePromptsClientQuery } from './index';

interface ContextStore {
  id: string;
  name: string;
}

interface ContextStoreConfig {
  id: string;
  name: string;
  generation: string;
  createdAt: string;
}

type ConfigDetails = {
  systemInstructions: string;
};

export const useStoreAIAutoReplySetting = ({
  channel,
  storeId,
}: {
  storeId: string;
  channel: ChatType;
}) => {
  const [contextStoreName, setContextStoreName] = useState('');
  const [contextId, setContextId] = useState('');

  const queryResult = useQuery(GET_STORE_AI_AUTO_REPLY_SETTING, {
    variables: {
      input: {
        channel,
        storeId,
      },
    },
    skip: !storeId || !channel,
  });

  const { data: contextStoreData, loading: fetchingContextStores } =
    usePromptsClientQuery<ContextStore[]>('/store-names');

  const { data: configData } = usePromptsClientQuery<ContextStoreConfig[]>(
    `/configs?storeName=${encodeURIComponent(contextStoreName)}`,
    !!contextStoreName
  );

  const { data: configDetails } = usePromptsClientQuery<ConfigDetails>(
    `/configs/${contextId}`,
    !!contextId
  );
  const systemInstructions = useMemo(
    () => configDetails?.systemInstructions || '',
    [configDetails]
  );

  const contextStoreOptions = useMemo(
    () => contextStoreData?.map(({ name }) => name),
    [contextStoreData]
  );

  const contextOptions = useMemo(() => configData?.map(({ id }) => id), [configData]);

  useEffect(() => {
    const contextConfigId = queryResult.data?.getStoreSetting?.contextConfigId;

    if (contextConfigId) {
      const storeName = contextConfigId.split('/')[0] || '';
      setContextStoreName(storeName);
      setContextId(contextConfigId);
    }
  }, [queryResult.data?.getStoreSetting?.contextConfigId]);

  const isLoading = useMemo(
    () => queryResult.loading || fetchingContextStores,
    [queryResult.loading, fetchingContextStores]
  );

  const hasAiFooter = queryResult.data?.getStoreSetting?.hasAiFooter ?? false;

  return {
    contextId,
    setContextId,
    contextStoreName,
    setContextStoreName,
    systemInstructions,
    contextStoreOptions,
    contextOptions,
    isLoading,
    hasAiFooter,
  };
};
